import React, { useState, useEffect, useRef } from 'react';

/**
 * DecryptionText Component
 * 
 * Displays text that appears "encrypted" (random characters) initially
 * and then "decrypts" to the actual text over a few seconds.
 * 
 * @param {string} text - The final text to display
 * @param {number} duration - Duration of the reveal effect in milliseconds (default: 2000)
 * @param {number} revealDelay - Delay before reveal starts in milliseconds (default: 1500)
 * @param {string} className - Optional CSS class name
 */
const DecryptionText = ({ text, duration = 2000, revealDelay = 1500, className = '' }) => {
    // Characters to use for the "encrypted" state
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';

    // Helper to scramble text
    const scrambleText = (txt) => {
        if (!txt) return '';
        let result = '';
        for (let i = 0; i < txt.length; i++) {
            if (txt[i] === ' ' || txt[i] === '\n') {
                result += txt[i];
            } else {
                result += chars[Math.floor(Math.random() * chars.length)];
            }
        }
        return result;
    };

    // Initialize with scrambled text so it renders encrypted immediately
    const [displayText, setDisplayText] = useState(() => scrambleText(text));
    const [isDecrypted, setIsDecrypted] = useState(false);

    useEffect(() => {
        // Reset state when text changes
        setIsDecrypted(false);

        if (!text) {
            setDisplayText('');
            return;
        }

        // Ensure we start with scrambled text (in case text prop changed)
        setDisplayText(scrambleText(text));

        let animationFrameId;
        let timeoutId;

        // Start the reveal animation after the delay
        timeoutId = setTimeout(() => {
            let startTime = Date.now();

            const updateText = () => {
                const currentTime = Date.now();
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                if (progress >= 1) {
                    setDisplayText(text);
                    setIsDecrypted(true);
                } else {
                    // Calculate how many characters should be revealed based on progress
                    const revealCount = Math.floor(text.length * progress);

                    // Construct the current string:
                    // 1. Revealed part (start of string)
                    // 2. Scrambled part (rest of string)
                    let currentString = text.substring(0, revealCount);

                    // Add random characters for the rest
                    for (let i = revealCount; i < text.length; i++) {
                        // Preserve spaces and newlines to keep layout stable
                        if (text[i] === ' ' || text[i] === '\n') {
                            currentString += text[i];
                        } else {
                            currentString += chars[Math.floor(Math.random() * chars.length)];
                        }
                    }

                    setDisplayText(currentString);
                    animationFrameId = requestAnimationFrame(updateText);
                }
            };

            animationFrameId = requestAnimationFrame(updateText);
        }, revealDelay);

        return () => {
            clearTimeout(timeoutId);
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [text, duration, revealDelay]);

    return (
        <span className={`decryption-text ${className} ${isDecrypted ? 'decrypted' : 'encrypting'}`}>
            {displayText}
        </span>
    );
};

export default DecryptionText;
